import { Injectable, signal } from '@angular/core';

/** Paso publicado por la vista activa: a que ruta pertenece y con que clave se nombra. */
export interface BreadcrumbStep {
  /** Ruta en la que la vista estaba cuando publico el paso. */
  route: string;

  /** Clave i18n del paso (p. ej. `BC.create`, o una propia del flujo). */
  key: string;
}

/**
 * Ultimo tramo del breadcrumb, publicado por la vista activa.
 *
 * El breadcrumb se arma leyendo la ruta, pero un CRUD no navega al pasar de
 * listado a formulario: solo cambia de modo. Por eso el ultimo tramo se
 * quedaba siempre en "Listado" aunque la pantalla dijera "Agregar". Aqui la
 * vista publica que paso esta mostrando y el breadcrumb lo refleja.
 *
 * Se publica la CLAVE y no el texto: asi el tramo se retraduce solo cuando
 * cambia el idioma, igual que el resto del breadcrumb.
 *
 * Vive en la capa compartida porque quien publica es el CRUD base y quien lo
 * consume es el layout, que ya depende de esta capa.
 */
@Injectable({ providedIn: 'root' })
export class BreadcrumbStepService {
  private readonly current = signal<BreadcrumbStep | null>(null);

  /** Paso vigente (o null si la vista activa no publica ninguno). */
  readonly step = this.current.asReadonly();

  /** Declara el paso que muestra la vista de `route`. */
  publish(route: string, key: string): void {
    if (!route || !key) return;
    const previo = this.current();
    if (previo?.route === route && previo?.key === key) return;
    this.current.set({ route, key });
  }

  /**
   * Retira el paso. Con `route`, solo si el vigente es el de esa ruta: evita
   * que una vista que se destruye borre el paso que otra acaba de publicar.
   */
  clear(route?: string): void {
    if (route && this.current()?.route !== route) return;
    this.current.set(null);
  }

  /** Clave del paso vigente para `route`, o null si no hay ninguno para ella. */
  keyFor(route: string): string | null {
    const step = this.current();
    return step && step.route === route ? step.key : null;
  }
}
